import React, { useRef } from "react";

import { mdiCog } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { useDrag, useDragLayer, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import type { JSX } from "react";

import type { ItemDisposition, ZoneDisposition } from "./Disposition";
import type { ItemDragType } from "../../../../constants/dragTypes";

interface SortableItemProps {
  item: ItemDisposition;
  acceptItemType: ItemDragType;
  onMove?: ({
    itemId,
    newRank,
    targetZone,
  }: {
    itemId: string;
    newRank: number;
    targetZone: ZoneDisposition;
  }) => void;
  targetZone: ZoneDisposition;
}

function SortableItem({
  targetZone,
  item,
  acceptItemType,
  onMove,
}: SortableItemProps): JSX.Element {
  const itemContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("editor-menu");

  const [, dragRef] = useDrag(
    () => ({
      type: acceptItemType,
      item,
    }),
    [item.id, item.rank, acceptItemType],
  );

  const { isDragging } = useDragLayer((monitor) => {
    const draggedItem = monitor.getItem();
    return {
      isDragging:
        monitor.isDragging() &&
        monitor.getItemType() === acceptItemType &&
        draggedItem?.id === item.id,
    };
  });

  const [, dropRef] = useDrop({
    accept: acceptItemType,
    hover(dragMedia: ItemDisposition, monitor) {
      if (!onMove) {
        return;
      }

      if (!itemContainerRef.current) {
        return;
      }

      const isSameItem = dragMedia.id === item.id;

      if (isSameItem) {
        return;
      }

      const dragRank = dragMedia.rank;
      const hoverRank = item.rank;

      const hoverBoundingRect =
        itemContainerRef.current?.getBoundingClientRect();
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientX = clientOffset.x - hoverBoundingRect.left;

      const isBeforeButTooHigh =
        dragRank < hoverRank && hoverClientX < hoverMiddleX;

      if (isBeforeButTooHigh) {
        return;
      }

      const isAfterButTooLow =
        dragRank > hoverRank && hoverClientX > hoverMiddleX;

      if (isAfterButTooLow) {
        return;
      }

      onMove({
        itemId: dragMedia.id,
        newRank: hoverRank,
        targetZone,
      });

      dragMedia.rank = hoverRank; // eslint-disable-line no-param-reassign
    },
  });

  const handleClickSettings = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    item.onClickSettings?.();
  };

  const hasSettings = Boolean(item.onClickSettings);

  const containerClassNames = classNames(
    "flex cursor-grab items-center text-foreground justify-center gap-[0.3125rem] flex-col h-[60px] relative",
    "rounded-[0.3125rem] border border-border bg-card px-[0.546875rem] py-[0.390625rem] transition-all hover:border-input active:cursor-grabbing hover:shadow-md",
    {
      "opacity-50": isDragging,
    },
  );

  dragRef(dropRef(itemContainerRef));

  return (
    <div className="flex shrink-0 rounded-[0.3125rem]">
      <div ref={itemContainerRef} className={containerClassNames}>
        {hasSettings ? (
          <div
            className="absolute top-0 right-0 cursor-pointer p-[0.15625rem]"
            data-tooltip-content={t("settings")}
            data-tooltip-id={APP_TOOLTIP_ID}
            onClick={handleClickSettings}
            role="button"
            tabIndex={0}
          >
            <Icon path={mdiCog} size={0.625} />
          </div>
        ) : null}
        {item.icon}
        <span className="leading-4">{item.label}</span>
      </div>
    </div>
  );
}

export default SortableItem;
